import React, {useEffect, useState} from "react";
import {
    Box,
    Typography,
    IconButton,
    Button,
    List,
    ListItem,
    ListItemAvatar,
    Avatar,
    ListItemText,
    TextField,
    Stack,
    Divider,
    Chip,
    Paper,
    Tooltip,
    Grid,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Modal
} from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ShareIcon from "@mui/icons-material/Share";
import DownloadIcon from "@mui/icons-material/Download";
import FolderOffIcon from "@mui/icons-material/FolderOff";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";
import {ApiDetails} from "../../../../dummyData";
import Backdrop from "@mui/material/Backdrop";
import {showSnackbar} from "../../../../components/SnackBar/SnackBar";
import {
    DeleteOutline,
    Add,
    Description,
    Image,
    PictureAsPdf,
    InsertDriveFile,
    FilePresent,
    Article,
    ZoomIn,
    ZoomOut
} from "@mui/icons-material";

export default function TenantFiles(props) {
    const {files} = props; // Access tenant files from props
    const [isEditing, setIsEditing] = useState(false); // State to toggle between views
    const [isLoading, setIsLoading] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const [previewScale, setPreviewScale] = useState(1);
    const [isImageViewer, setIsImageViewer] = useState(false);

    // State for file entries and categories
    const [fileEntries, setFileEntries] = useState([]);
    const [fileCategories, setFileCategories] = useState({
        identification: [],
        contract: [],
        inventory: [],
        photos: []
    });

    // Initialize file entries from the response
    useEffect(() => {
        try {
            // Check if files exist and have documents property
            if (files && typeof files === 'object') {
                // Handle both direct object and API response format with documents property
                const documents = files.documents || files;

                // Only proceed if documents is an object, not null, and not empty
                if (documents && typeof documents === 'object' && Object.keys(documents).length > 0) {
                    // Convert the documents object to entries array, filtering out defaults
                    const entries = Object.entries(documents).filter(
                        ([key, value]) => {
                            return value && value.base64 !== "No File Found" && key !== "defaults";
                        }
                    );

                    setFileEntries(entries);

                    // Organize files by category
                    const categories = {
                        identification: entries.filter(([key]) =>
                            key.includes('national_id') || key.includes('id_card')),
                        contract: entries.filter(([key]) =>
                            key.includes('contract') || key.includes('agreement') || key.includes('lease')),
                        inventory: entries.filter(([key]) =>
                            key.includes('inventory') || key.includes('checklist')),
                        photos: entries.filter(([key]) =>
                            key.includes('photo') || key.includes('picture') || key.includes('image'))
                    };

                    setFileCategories(categories);
                } else {
                    // Reset to empty if no valid documents
                    setFileEntries([]);
                    setFileCategories({
                        identification: [],
                        contract: [],
                        inventory: [],
                        photos: []
                    });
                }
            }
        } catch (error) {
            console.error("Error processing file data:", error);
            setFileEntries([]);
        }
    }, [files]);

    // Function to determine file icon based on extension
    const getFileIcon = (extension) => {
        if (!extension) return <FilePresent />;

        switch(extension.toLowerCase()) {
            case 'pdf':
                return <PictureAsPdf />;
            case 'jpg':
            case 'jpeg':
            case 'png':
                return <Image />;
            case 'doc':
            case 'docx':
                return <Description />;
            default:
                return <FilePresent />;
        }
    };

    // Function to format file size
    const formatFileSize = (sizeInKB) => {
        if (!sizeInKB) return "Unknown size";

        if (sizeInKB < 1024) {
            return `${sizeInKB} KB`;
        } else {
            return `${(sizeInKB / 1024).toFixed(2)} MB`;
        }
    };

    // Placeholder function for sharing a file
    const handleShare = (fileName) => {
        alert(`Sharing file: ${fileName}`);
    };

    // Enhanced function for previewing a file
    const handlePreview = (file) => {
        if (!file || !file.base64 || file.base64 === "No File Found") {
            alert("File not found for preview.");
            return;
        }

        setPreviewFile(file);

        // Check if it's an image file
        const isImage = ['jpg', 'jpeg', 'png', 'gif'].includes(file.file_type?.toLowerCase() || file.extension?.toLowerCase());
        setIsImageViewer(isImage);
        setPreviewScale(1); // Reset zoom level
        setPreviewOpen(true);
    };

    // Function to get file data URL
    const getFileDataUrl = (file) => {
        if (!file || !file.base64) return '';

        const fileType = file.file_type || file.extension || 'octet-stream';

        // Handle image files
        if (['jpg', 'jpeg', 'png', 'gif'].includes(fileType.toLowerCase())) {
            return `data:image/${fileType.toLowerCase()};base64,${file.base64}`;
        }

        // Handle PDF files
        if (fileType.toLowerCase() === 'pdf') {
            return `data:application/pdf;base64,${file.base64}`;
        }

        // Handle other file types
        return `data:application/${fileType};base64,${file.base64}`;
    };

    // Close the preview dialog
    const handleClosePreview = () => {
        setPreviewOpen(false);
        setPreviewFile(null);
    };

    // Handle zoom in/out for images
    const handleZoomIn = () => {
        setPreviewScale(prev => Math.min(prev + 0.25, 3));
    };

    const handleZoomOut = () => {
        setPreviewScale(prev => Math.max(prev - 0.25, 0.5));
    };

    // Enhanced function for downloading a file
    const handleDownload = (file) => {
        if (!file || !file.base64 || file.base64 === "No File Found") {
            alert("File not found for download.");
            return;
        }

        const fileType = file.file_type || file.extension || 'octet-stream';
        const filename = file.filename || `file.${fileType}`;

        const link = document.createElement("a");
        link.href = getFileDataUrl(file);
        link.download = filename;
        link.click();
    };

    //Tenant Photo
    const [tenantPhoto, setTenantPhoto] = useState("");
    const [photo, setPhoto] = useState(null);

    //Tenant National ID
    const [photoOfNationalID, setPhotoOfNationalID] = useState("");
    const [nationalID, setNationalID] = useState(null);

    //Tenant Contract - Modified to support multiple files
    const [contractFiles, setContractFiles] = useState([]);
    const [contractFileNames, setContractFileNames] = useState([]);

    //Tenant Checklist
    const [inventoryChecklist, setInventoryChecklist] = useState("");
    const [checklist, setChecklist] = useState(null);

    function handleFile1(e) {
        if (e.target.files && e.target.files.length > 0) {
            let filename = getFileName(e);
            setPhoto(filename);
            setTenantPhoto(e.target.files[0]);
        }
    }

    function handleFile2(e) {
        if (e.target.files && e.target.files.length > 0) {
            let filename = getFileName(e);
            setNationalID(filename);
            setPhotoOfNationalID(e.target.files[0]);
        }
    }

    // Modified to handle multiple contract files
    function handleContractFile(e) {
        if (e.target.files && e.target.files.length > 0) {
            let filename = getFileName(e);
            // Add the new file to the existing contract files
            setContractFiles([...contractFiles, e.target.files[0]]);
            setContractFileNames([...contractFileNames, filename]);
        }
    }

    function handleFile4(e) {
        if (e.target.files && e.target.files.length > 0) {
            let filename = getFileName(e);
            setChecklist(filename);
            setInventoryChecklist(e.target.files[0]);
        }
    }

    const getFileName = (e) => {
        if (!e.target.value) return "Unknown file";

        let startIndex =
            e.target.value.indexOf("\\") >= 0
                ? e.target.value.lastIndexOf("\\")
                : e.target.value.lastIndexOf("/");
        let filename = e.target.value.substring(startIndex);
        if (filename.indexOf("\\") === 0 || filename.indexOf("/") === 0) {
            filename = filename.substring(1);
        }
        return filename;
    };

    // Remove a specific contract file
    const removeContractFile = (index) => {
        const updatedFiles = [...contractFiles];
        const updatedFileNames = [...contractFileNames];
        updatedFiles.splice(index, 1);
        updatedFileNames.splice(index, 1);
        setContractFiles(updatedFiles);
        setContractFileNames(updatedFileNames);
    };

    // Initialize form data from props
    useEffect(() => {
        try {
            // Extract defaults from props
            const defaults = files?.defaults || {};

            // Tenant photo
            if (defaults.tenant_photo && defaults.tenant_photo.trim && defaults.tenant_photo.trim() !== "") {
                try {
                    const photoName = defaults.tenant_photo
                        .toString()
                        .split("FILE_EXTENSION")[0]
                        .split("/")[
                    defaults.tenant_photo
                        .toString()
                        .split("FILE_EXTENSION")[0]
                        .split("/").length - 1
                        ];
                    setPhoto(photoName);
                    setTenantPhoto(defaults.tenant_photo);
                } catch (error) {
                    console.error("Error processing tenant photo filename:", error);
                }
            }

            // National ID
            if (defaults.photo_of_national_id && defaults.photo_of_national_id.trim && defaults.photo_of_national_id.trim() !== "") {
                try {
                    const idName = defaults.photo_of_national_id
                        .toString()
                        .split("FILE_EXTENSION")[0]
                        .split("/")[
                    defaults.photo_of_national_id
                        .toString()
                        .split("FILE_EXTENSION")[0]
                        .split("/").length - 1
                        ];
                    setNationalID(idName);
                    setPhotoOfNationalID(defaults.photo_of_national_id);
                } catch (error) {
                    console.error("Error processing national ID filename:", error);
                }
            }

            // Contract
            if (defaults.copy_of_contract && defaults.copy_of_contract.trim && defaults.copy_of_contract.trim() !== "") {
                try {
                    // For backward compatibility, add existing contract as first item
                    const existingContract = defaults.copy_of_contract;
                    const contractName = existingContract
                        .toString()
                        .split("FILE_EXTENSION")[0]
                        .split("/")[
                    existingContract
                        .toString()
                        .split("FILE_EXTENSION")[0]
                        .split("/").length - 1
                        ];

                    setContractFiles([existingContract]);
                    setContractFileNames([contractName]);
                } catch (error) {
                    console.error("Error processing contract filename:", error);
                }
            }

            // Inventory checklist
            if (defaults.inventory_checklist && defaults.inventory_checklist.trim && defaults.inventory_checklist.trim() !== "") {
                try {
                    const checklistName = defaults.inventory_checklist
                        .toString()
                        .split("FILE_EXTENSION")[0]
                        .split("/")[
                    defaults.inventory_checklist
                        .toString()
                        .split("FILE_EXTENSION")[0]
                        .split("/").length - 1
                        ];
                    setChecklist(checklistName);
                    setInventoryChecklist(defaults.inventory_checklist);
                } catch (error) {
                    console.error("Error processing inventory checklist filename:", error);
                }
            }
        } catch (error) {
            console.error("Error initializing form data:", error);
        }
    }, [files]);

    const updateTenantFiles = () => {
        setIsLoading(true);

        let formData = new FormData();

        // Basic tenant details
        const tenantDetails = {
            tenant_id: props.tenantDetails?.tenant_id,
            landlord_id: props.originalText ? JSON.parse(props.originalText)[0][1] : null,
            property_name: props.propertyName || "",
            property_id: props.propertyID || "",
            contract_files_count: contractFiles.length
        };

        // Only include file paths for files that are being updated
        const filesToUpdate = {};

        // Check if each file type has new uploads
        if (photoOfNationalID instanceof File) {
            filesToUpdate.photo_of_national_id = true;
        }
        if (inventoryChecklist instanceof File) {
            filesToUpdate.inventory_checklist = true;
        }
        if (tenantPhoto instanceof File) {
            filesToUpdate.tenant_photo = true;
        }
        if (contractFiles.some(file => file instanceof File)) {
            filesToUpdate.copy_of_contract = true;
        }

        // Add the files to update indicator to tenant details
        tenantDetails.files_to_update = filesToUpdate;

        formData.append("details", JSON.stringify(tenantDetails));

        // Append basic files only if they are new File objects
        if (photoOfNationalID instanceof File) {
            formData.append("photo_of_national_id", photoOfNationalID);
        }
        if (inventoryChecklist instanceof File) {
            formData.append("inventory_checklist", inventoryChecklist);
        }
        if (tenantPhoto instanceof File) {
            formData.append("tenant_photo", tenantPhoto);
        }

        // Append multiple contract files only if they are new File objects
        contractFiles.forEach((file, index) => {
            if (file instanceof File) {
                formData.append(`contract_file_${index}`, file);
            }
        });

        fetch(ApiDetails + "pegasus/visionary/tenant/update/tenant/files", {
            method: "POST",
            headers: {
                "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
            },
            body: formData,
        })
            .then(async (response) => {
                let data = await response.json();
                setIsLoading(false);

                if (response.status === 200) {
                    showSnackbar({
                        title: "Success!",
                        message: "Tenant files updated successfully.",
                        type: "success",
                        position: {vertical: "bottom", horizontal: "right"},
                    });

                    // Switch back to view mode after successful upload
                    setIsEditing(false);

                    // Refresh the files if there's a callback
                    if (props.onFilesUpdated && typeof props.onFilesUpdated === 'function') {
                        props.onFilesUpdated();
                    }
                } else {
                    showSnackbar({
                        title: "Error!",
                        message: data.message || "Failed updating tenant files.",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
            })
            .catch((err) => {
                console.error(err);
                setIsLoading(false);

                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong. Try again later.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            });
    };
    // Render a file category section
    const renderFileCategory = (title, files) => {
        if (!files || !Array.isArray(files) || files.length === 0) return null;

        return (
            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="#2C5283" sx={{ mb: 1 }}>
                    {title} ({files.length})
                </Typography>
                <Grid container spacing={2}>
                    {files.map(([key, fileData], index) => {
                        if (!fileData) return null;

                        // Prepare file thumbnail if it's an image
                        const isImage = ['jpg', 'jpeg', 'png', 'gif'].includes(
                            (fileData.file_type || fileData.extension || '').toLowerCase()
                        );

                        return (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <Paper
                                    elevation={1}
                                    sx={{
                                        p: 2,
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        position: 'relative',
                                        borderRadius: '8px',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
                                            transform: 'translateY(-2px)'
                                        }
                                    }}
                                >
                                    {/* File Header */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Avatar sx={{ bgcolor: '#2C5283', mr: 1 }}>
                                            {getFileIcon(fileData.file_type || fileData.extension)}
                                        </Avatar>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                fontWeight: 'bold',
                                                textOverflow: 'ellipsis',
                                                overflow: 'hidden',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {fileData.name || key.replace(/_/g, " ")}
                                        </Typography>
                                    </Box>

                                    {/* File Preview Thumbnail */}
                                    {isImage && fileData.base64 && (
                                        <Box
                                            sx={{
                                                width: '100%',
                                                height: 120,
                                                mb: 2,
                                                borderRadius: '4px',
                                                overflow: 'hidden',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                bgcolor: '#f5f8ff',
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => handlePreview(fileData)}
                                        >
                                            <img
                                                src={`data:image/${fileData.file_type || fileData.extension};base64,${fileData.base64}`}
                                                alt={fileData.name || key}
                                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                            />
                                        </Box>
                                    )}

                                    {/* File Info */}
                                    <Box sx={{ mb: 1 }}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', mb: 0.5 }}
                                        >
                                            <strong>Type:</strong>&nbsp;
                                            {(fileData.file_type || fileData.extension || "Unknown").toUpperCase()}
                                        </Typography>
                                        {fileData.size_kb && (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center' }}
                                            >
                                                <strong>Size:</strong>&nbsp;
                                                {formatFileSize(fileData.size_kb)}
                                            </Typography>
                                        )}
                                        {fileData.filename && (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    textOverflow: 'ellipsis',
                                                    overflow: 'hidden',
                                                    whiteSpace: 'nowrap',
                                                    display: 'flex'
                                                }}
                                            >
                                                <strong>File:</strong>&nbsp;
                                                <span style={{
                                                    textOverflow: 'ellipsis',
                                                    overflow: 'hidden',
                                                    whiteSpace: 'nowrap'
                                                }}>{fileData.filename}</span>
                                            </Typography>
                                        )}
                                    </Box>

                                    {/* Action Buttons */}
                                    <Box sx={{ display: 'flex', mt: 'auto', justifyContent: 'space-between' }}>
                                        <Tooltip title="Preview">
                                            <IconButton
                                                size="small"
                                                disabled={!fileData.base64 || fileData.base64 === "No File Found"}
                                                onClick={() => handlePreview(fileData)}
                                                sx={{ color: '#2C5283' }}
                                            >
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Download">
                                            <IconButton
                                                size="small"
                                                disabled={!fileData.base64 || fileData.base64 === "No File Found"}
                                                onClick={() => handleDownload(fileData)}
                                                sx={{ color: '#2C5283' }}
                                            >
                                                <DownloadIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Paper>
                            </Grid>
                        );
                    })}
                </Grid>
            </Box>
        );
    };

    return (
        <Box
            sx={{
                padding: "20px",
                backgroundColor: "#F7F9FC",
                borderRadius: "8px",
                boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
                position: "relative"
            }}
        >
            {isLoading && (
                <Backdrop
                    sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                    open={isLoading}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>
            )}

            {/* File Preview Dialog */}
            <Dialog
                open={previewOpen}
                onClose={handleClosePreview}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        height: isImageViewer ? '80vh' : '90vh',
                        maxHeight: isImageViewer ? '80vh' : '90vh',
                    }
                }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                        {previewFile?.name || previewFile?.filename || "File Preview"}
                    </Typography>
                    <Box>
                        {isImageViewer && (
                            <>
                                <IconButton onClick={handleZoomOut} disabled={previewScale <= 0.5}>
                                    <ZoomOut />
                                </IconButton>
                                <IconButton onClick={handleZoomIn} disabled={previewScale >= 3}>
                                    <ZoomIn />
                                </IconButton>
                            </>
                        )}
                        <IconButton onClick={handleClosePreview}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{
                    padding: isImageViewer ? 1 : 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    overflow: 'auto'
                }}>
                    {previewFile && (
                        isImageViewer ? (
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '100%',
                                height: '100%',
                                overflow: 'auto'
                            }}>
                                <img
                                    src={getFileDataUrl(previewFile)}
                                    alt={previewFile.name || previewFile.filename || "Preview"}
                                    style={{
                                        maxWidth: '100%',
                                        transform: `scale(${previewScale})`,
                                        transformOrigin: 'center',
                                        transition: 'transform 0.2s'
                                    }}
                                />
                            </Box>
                        ) : (
                            <Box sx={{ width: '100%', height: '100%' }}>
                                <embed
                                    src={getFileDataUrl(previewFile)}
                                    type={`application/${previewFile.file_type || previewFile.extension || 'pdf'}`}
                                    width="100%"
                                    height="100%"
                                />
                            </Box>
                        )
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => handleDownload(previewFile)}
                        variant="contained"
                        startIcon={<DownloadIcon />}
                    >
                        Download
                    </Button>
                    <Button onClick={handleClosePreview} variant="outlined">Close</Button>
                </DialogActions>
            </Dialog>

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 3
                }}
            >
                <Typography
                    variant="h6"
                    fontWeight="bold"
                    color={"#2C5283"}
                >
                    Tenant Files
                </Typography>

                <Button
                    variant="contained"
                    color={isEditing ? "secondary" : "primary"}
                    sx={{
                        textTransform: "none",
                        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                    }}
                    onClick={() => setIsEditing(!isEditing)}
                >
                    {isEditing ? "Cancel Editing" : "Edit Files"}
                </Button>
            </Box>

            {isEditing ? (
                // File Upload Form with input boxes
                <Paper
                    elevation={0}
                    sx={{
                        backgroundColor: "#FFF",
                        padding: "24px",
                        borderRadius: "8px",
                        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <UploadFileIcon sx={{ fontSize: "32px", color: "#2C5283", mr: 2 }} />
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: "bold",
                                color: "#2C5283",
                            }}
                        >
                            Upload Tenant Files
                        </Typography>
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    <Stack spacing={3} sx={{ width: "100%" }}>
                        {/* Tenant Photo */}
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px' }}>
                            <Typography variant="subtitle1" fontWeight="bold" color="#2C5283" gutterBottom>
                                Tenant Photo
                            </Typography>

                            {tenantPhoto === "" ? (
                                <TextField
                                    type="file"
                                    onChange={(event) => handleFile1(event)}
                                    fullWidth
                                    inputProps={{ accept: ".jpg,.jpeg,.png" }}
                                    label={`Upload tenant photo`}
                                    helperText={"Accepted formats: JPG, JPEG, PNG"}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                />
                            ) : (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        p: 1,
                                        bgcolor: "#f5f8ff",
                                        borderRadius: '4px'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Avatar sx={{ bgcolor: '#e3f2fd', color: '#2C5283', mr: 2 }}>
                                            <Image />
                                        </Avatar>
                                        <Typography>{photo}</Typography>
                                    </Box>
                                    <IconButton
                                        onClick={() => {
                                            setTenantPhoto("");
                                            setPhoto(null);
                                        }}
                                    >
                                        <DeleteOutline />
                                    </IconButton>
                                </Box>
                            )}
                        </Paper>

                        {/* National ID */}
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px' }}>
                            <Typography variant="subtitle1" fontWeight="bold" color="#2C5283" gutterBottom>
                                National ID
                            </Typography>

                            {photoOfNationalID === "" ? (
                                <TextField
                                    type="file"
                                    onChange={(event) => handleFile2(event)}
                                    fullWidth
                                    inputProps={{ accept: ".pdf,.jpg,.jpeg,.png" }}
                                    label={`Upload National ID`}
                                    helperText={"Accepted formats: PDF, JPG, JPEG, PNG"}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                />
                            ) : (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        p: 1,
                                        bgcolor: "#f5f8ff",
                                        borderRadius: '4px'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Avatar sx={{ bgcolor: '#e3f2fd', color: '#2C5283', mr: 2 }}>
                                            <Article />
                                        </Avatar>
                                        <Typography>{nationalID}</Typography>
                                    </Box>
                                    <IconButton
                                        onClick={() => {
                                            setPhotoOfNationalID("");
                                            setNationalID(null);
                                        }}
                                    >
                                        <DeleteOutline />
                                    </IconButton>
                                </Box>
                            )}
                        </Paper>

                        {/* Contract Files Section - Modified for multiple files */}
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px' }}>
                            <Typography variant="subtitle1" fontWeight="bold" color="#2C5283" gutterBottom>
                                Contract Files ({contractFiles.length} files)
                            </Typography>

                            {/* Display already added contract files */}
                            {contractFileNames.length > 0 && (
                                <List dense sx={{ mb: 2, bgcolor: '#f5f8ff', borderRadius: '4px' }}>
                                    {contractFileNames.map((fileName, index) => (
                                        <ListItem
                                            key={index}
                                            secondaryAction={
                                                <IconButton edge="end" onClick={() => removeContractFile(index)}>
                                                    <DeleteOutline />
                                                </IconButton>
                                            }
                                        >
                                            <ListItemAvatar>
                                                <Avatar sx={{ bgcolor: '#e3f2fd', color: '#2C5283' }}>
                                                    <InsertDriveFileIcon />
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={`Contract Page ${index + 1}`}
                                                secondary={fileName}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            )}

                            {/* Button to add a new contract file */}
                            <Button
                                component="label"
                                variant="outlined"
                                startIcon={<Add />}
                                sx={{ mt: 1 }}
                                color="primary"
                            >
                                Add Contract File
                                <input
                                    type="file"
                                    hidden
                                    onChange={(event) => handleContractFile(event)}
                                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                />
                            </Button>
                            <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                                Add multiple pages of the contract. Accepted formats: PDF, DOC, DOCX, JPG, JPEG, PNG
                            </Typography>
                        </Paper>

                        {/* Inventory Checklist */}
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px' }}>
                            <Typography variant="subtitle1" fontWeight="bold" color="#2C5283" gutterBottom>
                                Inventory Checklist
                            </Typography>

                            {inventoryChecklist === "" ? (
                                <TextField
                                    type="file"
                                    onChange={(event) => handleFile4(event)}
                                    fullWidth
                                    inputProps={{ accept: ".pdf,.doc,.docx,.xlsx,.xls" }}
                                    label={`Upload inventory checklist`}
                                    helperText={"Accepted formats: PDF, DOC, DOCX, XLS, XLSX"}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                />
                            ) : (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        p: 1,
                                        bgcolor: "#f5f8ff",
                                        borderRadius: '4px'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Avatar sx={{ bgcolor: '#e3f2fd', color: '#2C5283', mr: 2 }}>
                                            <Description />
                                        </Avatar>
                                        <Typography>{checklist}</Typography>
                                    </Box>
                                    <IconButton
                                        onClick={() => {
                                            setInventoryChecklist("");
                                            setChecklist(null);
                                        }}
                                    >
                                        <DeleteOutline />
                                    </IconButton>
                                </Box>
                            )}
                        </Paper>
                    </Stack>

                    {/* Submit Button */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                        <Button
                            variant="contained"
                            sx={{
                                backgroundColor: "#2C5283",
                                color: "#FFF",
                                fontWeight: "bold",
                                textTransform: "none",
                                padding: "10px 24px",
                                borderRadius: "8px",
                                boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
                                "&:hover": {
                                    backgroundColor: "#1A365D",
                                },
                            }}
                            onClick={updateTenantFiles}
                            disabled={isLoading}
                        >
                            {isLoading ? "Uploading..." : "Upload Files"}
                        </Button>
                    </Box>
                </Paper>
            ) : fileEntries.length > 0 ? (
                // Improved file display view with categories
                <Paper
                    elevation={0}
                    sx={{
                        backgroundColor: "#FFF",
                        padding: "24px",
                        borderRadius: "8px",
                        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
                    }}
                >
                    {/* Summary section */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {fileEntries.length} file{fileEntries.length !== 1 ? 's' : ''} available
                        </Typography>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {fileCategories.identification.length > 0 && (
                                <Chip
                                    label={`Identification (${fileCategories.identification.length})`}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                />
                            )}
                            {fileCategories.contract.length > 0 && (
                                <Chip
                                    label={`Contract (${fileCategories.contract.length})`}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                />
                            )}
                            {fileCategories.inventory.length > 0 && (
                                <Chip
                                    label={`Inventory (${fileCategories.inventory.length})`}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                />
                            )}
                            {fileCategories.photos.length > 0 && (
                                <Chip
                                    label={`Photos (${fileCategories.photos.length})`}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                />
                            )}
                        </Box>
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    {/* Categorized file sections */}
                    {renderFileCategory("Identification Documents", fileCategories.identification)}
                    {renderFileCategory("Contract Documents", fileCategories.contract)}
                    {renderFileCategory("Inventory Documents", fileCategories.inventory)}
                    {renderFileCategory("Photos", fileCategories.photos)}

                    {/* Any files that don't fit the categories */}
                    {fileEntries.length > 0 &&
                        fileCategories.identification.length +
                        fileCategories.contract.length +
                        fileCategories.inventory.length +
                        fileCategories.photos.length < fileEntries.length && (
                            renderFileCategory("Other Documents", fileEntries.filter(([key]) =>
                                !key.includes('national_id') && !key.includes('id_card') &&
                                !key.includes('contract') && !key.includes('agreement') && !key.includes('lease') &&
                                !key.includes('inventory') && !key.includes('checklist') &&
                                !key.includes('photo') && !key.includes('picture') && !key.includes('image')
                            ))
                        )}
                </Paper>
            ) : (
                <Paper
                    elevation={0}
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#fff",
                        padding: "40px",
                        borderRadius: "8px",
                        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
                    }}
                >
                    <FolderOffIcon sx={{ fontSize: "60px", color: "#9CA3AF", mb: 2 }} />
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: "bold",
                            color: "#9CA3AF",
                        }}
                    >
                        No Tenant Files Found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: "center", maxWidth: "80%" }}>
                        There are no files uploaded for this tenant. Click "Edit Files" to add tenant documents.
                    </Typography>
                    <Button
                        variant="contained"
                        sx={{
                            mt: 3,
                            backgroundColor: "#2C5283",
                            color: "#FFF",
                            fontWeight: "medium",
                            textTransform: "none",
                            borderRadius: "8px",
                        }}
                        onClick={() => setIsEditing(true)}
                    >
                        Upload Files
                    </Button>
                </Paper>
            )}
        </Box>
    );
}